import React, { useMemo } from 'react'
import { Alert, Modal, Spin, message } from '@surecompanies/core_components'

import { copyToClipboard } from 'components/CopyLink/CopyText'
import { usePaymentAssignmentDetailQuery } from '../../api/finance-api-client'
import { mapDetailToPaymentCase } from '../PaymentAssignment/Utils/paymentAssignmentMappers'
import { PaymentManagementSection } from '../PaymentAssignment/VcnPaymentManagement'

export interface PrimaryPaymentMethodTarget {
  benefitLabel: string
  paymentAssignmentId: number
}

interface Props {
  target: PrimaryPaymentMethodTarget | null
  onClose: () => void
  onSaved: () => void
}

/**
 * Payment Assignment's "Primary Payment Method" section, opened from the
 * reconciliation benefit grid. It edits the benefit through the same component
 * and requests as Payment Assignment's detail modal, loaded from one of the
 * benefit's payment assignments.
 */
const PrimaryPaymentMethodModal: React.FC<Props> = ({
  target,
  onClose,
  onSaved,
}) => {
  const paymentAssignmentId = target?.paymentAssignmentId ?? null
  const { data, isLoading, isError } =
    usePaymentAssignmentDetailQuery(paymentAssignmentId)

  const paymentCase = useMemo(
    () =>
      data && paymentAssignmentId !== null
        ? mapDetailToPaymentCase(data, paymentAssignmentId)
        : null,
    [data, paymentAssignmentId]
  )

  // Same rule as Payment Assignment's detail modal: a migrated child was
  // superseded by a newer assignment, so its payment method is read-only.
  const isMigratedChild =
    paymentCase?.migrateToId != null && paymentCase.migrateToId !== ''

  const handleCopy = (text: string) => {
    const trimmed = text?.trim()
    if (!trimmed || trimmed === 'N/A') return

    void copyToClipboard(trimmed).then((ok) => {
      if (ok) message.success('Copied to clipboard')
    })
  }

  return (
    <Modal
      open={target !== null}
      title={target ? `Primary payment method — ${target.benefitLabel}` : ''}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {isLoading && <Spin />}
      {isError && (
        <Alert
          type="error"
          message="Couldn't load the payment method. Try again."
        />
      )}
      {paymentCase && (
        <PaymentManagementSection
          paymentCase={paymentCase}
          onCopy={handleCopy}
          canUpdateCase={!isMigratedChild}
          onComplete={onSaved}
        />
      )}
    </Modal>
  )
}

export default PrimaryPaymentMethodModal
